import { useScrollReveal } from '../hooks/useScrollReveal';
import { SEOHead } from '../components/SEOHead';

export const PrivacyPolicyPage = () => {
  const pageRef = useScrollReveal();
  const effectiveDate = 'June 16, 2026';

  return (
    <main className="pt-24" ref={pageRef}>
      <SEOHead path="/privacy" />
      <section className="py-14 md:py-20 bg-cream">
        <div className="container-padding">
          <div className="max-w-3xl mx-auto">
            <span className="reveal overline-text mb-4 block">Legal</span>
            <h1 className="reveal reveal-delay-1 text-3xl md:text-4xl lg:text-5xl font-medium mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Privacy Policy
            </h1>
            <p className="reveal reveal-delay-2 text-sm text-[#9A9A9A]">Effective Date: {effectiveDate}</p>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="container-padding">
          <div className="max-w-3xl mx-auto prose prose-lg prose-pink prose-headings:font-semibold prose-headings:text-[#2A2A2A] prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3 prose-p:text-[#4A4A4A] prose-p:leading-relaxed prose-p:mb-4 prose-li:text-[#4A4A4A] prose-ul:my-4 prose-li:my-1 prose-a:text-[#E84993] prose-strong:text-[#2A2A2A]" data-testid="privacy-content">

            <h2>1. Introduction</h2>
            <p>
              Welcome to <strong>iamnatashasimone.com</strong> (the "Site"), operated by Yellow Mango Production ("we," "us," or "our").
              This Privacy Policy describes how we collect, use, disclose, and protect your personal information when you visit our Site or use our services.
              We are committed to protecting your privacy and handling your data responsibly.
            </p>

            <h2>2. Information We Collect</h2>
            <h3>Information You Provide</h3>
            <ul>
              <li><strong>Contact Information:</strong> Name, email address, phone number when you submit a contact form, book a call, or make an inquiry.</li>
              <li><strong>Order Information:</strong> Billing details, shipping address, and payment information when you purchase products (e.g., books).</li>
              <li><strong>Waitlist Information:</strong> Email address when you join a product waitlist.</li>
              <li><strong>Event Registrations:</strong> Name and contact details when you register for events.</li>
            </ul>

            <h3>Information Collected Automatically</h3>
            <ul>
              <li><strong>Usage Data:</strong> Pages visited, time spent, referring URL, browser type, device information, and IP address.</li>
              <li><strong>Cookies and Tracking Technologies:</strong> We use cookies and similar technologies to analyze traffic and improve your experience. See Section 6 for details.</li>
            </ul>

            <h2>3. How We Use Your Information</h2>
            <p>We use your personal information for the following purposes:</p>
            <ul>
              <li>To respond to inquiries, booking requests, and provide customer support</li>
              <li>To process orders and deliver products or services</li>
              <li>To send updates about products, events, and services you've expressed interest in</li>
              <li>To improve our website, content, and user experience</li>
              <li>To analyze site traffic and understand how visitors interact with our Site</li>
              <li>To comply with legal obligations and protect our rights</li>
            </ul>

            <h2>4. How We Share Your Information</h2>
            <p>We do not sell your personal information. We may share information with:</p>
            <ul>
              <li><strong>Service Providers:</strong> Third-party vendors that help us operate the Site (hosting, analytics, payment processing). These providers are contractually obligated to protect your data.</li>
              <li><strong>Legal Requirements:</strong> If required by law, subpoena, or other legal process.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
            </ul>

            <h2>5. Your Privacy Rights</h2>

            <h3>All Users</h3>
            <p>Regardless of where you reside, you may:</p>
            <ul>
              <li>Request access to the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt out of marketing communications at any time</li>
            </ul>

            <h3 id="california-rights">California Residents (CCPA/CPRA)</h3>
            <p>
              If you are a California resident, the California Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA)
              provide you with additional rights regarding your personal information:
            </p>
            <ul>
              <li><strong>Right to Know:</strong> You have the right to request that we disclose the categories and specific pieces of personal information we have collected about you, the categories of sources, the business purpose for collecting it, and the categories of third parties with whom we share it.</li>
              <li><strong>Right to Delete:</strong> You may request that we delete your personal information, subject to certain exceptions.</li>
              <li><strong>Right to Correct:</strong> You may request that we correct inaccurate personal information.</li>
              <li><strong>Right to Opt-Out of Sale/Sharing:</strong> We do not currently sell or share your personal information as defined under the CCPA/CPRA. If this changes in the future, we will provide a clear opt-out mechanism.</li>
              <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising any of your privacy rights.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:hello@iamnatashasimone.com" className="text-[#E84993]">hello@iamnatashasimone.com</a>.
              We will respond to verifiable requests within 45 days.
            </p>

            <h2>6. Cookies and Tracking Technologies</h2>
            <p>We use the following types of cookies:</p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for basic site functionality (e.g., session management, security). These cannot be disabled.</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use the Site (e.g., PostHog). These are only activated with your consent.</li>
              <li><strong>Marketing Cookies:</strong> Used for advertising and retargeting purposes. Not currently active, but may be used in the future with your consent.</li>
            </ul>
            <p>
              You can manage your cookie preferences at any time by clicking "Cookie Preferences" in our website footer.
              You can also control cookies through your browser settings.
            </p>

            <h2>7. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access,
              alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure,
              and we cannot guarantee absolute security.
            </p>

            <h2>8. Data Retention</h2>
            <p>
              We retain your personal information only for as long as necessary to fulfill the purposes described in this policy,
              or as required by law. When your data is no longer needed, we will securely delete or anonymize it.
            </p>

            <h2>9. Third-Party Links</h2>
            <p>
              Our Site may contain links to third-party websites. We are not responsible for the privacy practices of these websites.
              We encourage you to review their privacy policies before providing any personal information.
            </p>

            <h2>10. Children's Privacy</h2>
            <p>
              Our Site is not directed to children under 13. We do not knowingly collect personal information from children under 13.
              If you believe we have collected such information, please contact us and we will promptly delete it.
            </p>

            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we do, we will revise the "Effective Date" at the top of this page.
              We encourage you to review this policy periodically for any changes.
            </p>

            <h2>12. Contact Us</h2>
            <p>If you have any questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:</p>
            <ul>
              <li><strong>Email:</strong> <a href="mailto:hello@iamnatashasimone.com" className="text-[#E84993]">hello@iamnatashasimone.com</a></li>
              <li><strong>Address:</strong> Yellow Mango Production, 401 N. Ashley Dr., Suite #173011, Tampa, Florida 33602</li>
            </ul>

          </div>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicyPage;
